import { Stack, Typography } from '@mui/material';
import { Avatar } from '@/components';
import { TableRowLink } from '@/components/table';
import { clientManagementPath } from '@/features/users/clients/routes/paths';
import { ReferralUser } from '../types';
import { getReferralUserDisplayName } from '../utils';

type ReferralUserCellProps = {
  user?: ReferralUser | null;
  email?: string | null;
  userId?: number | string | null;
  fallback: string;
  neutralText?: string;
};

export default function ReferralUserCell({
  user,
  email,
  userId,
  fallback,
  neutralText,
}: ReferralUserCellProps) {
  const displayName =
    getReferralUserDisplayName(user, '') ||
    email ||
    (userId ? `User #${userId}` : '') ||
    neutralText ||
    fallback;
  const resolvedUserId = user?.id ?? userId;
  const detailPath = resolvedUserId
    ? clientManagementPath('edit', {
        userId: String(resolvedUserId),
        tabSid: 'details',
      })
    : null;

  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{ minWidth: 0 }}
    >
      <Avatar
        src={user?.profile_image ?? undefined}
        alt={displayName}
        sx={{ width: 32, height: 32, flexShrink: 0 }}
      >
        {displayName.charAt(0).toUpperCase()}
      </Avatar>
      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
        {detailPath ? (
          <TableRowLink
            linkText={displayName}
            linkTo={detailPath}
            noWrap
            sx={{ px: 0, py: 0 }}
          />
        ) : (
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {displayName}
          </Typography>
        )}
        {email && email !== displayName && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {email}
          </Typography>
        )}
        {neutralText && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {neutralText}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
